const guide = `# Anbieterportal-Dokumentation

## Übersicht

Das Anbieterportal ermöglicht es Anbietern, eSIMs anzufordern, Inventar zu verwalten, eSIMs Kunden zuzuweisen und den Aktivierungsstatus zu verfolgen. Diese Anleitung beschreibt den vollständigen Workflow, verfügbare APIs, UI-Anforderungen und Systemverhalten.

**Basis-API-Pfad:** \`https://r4lsolutions.com/backend/api/provider\`

> Alle Endpunkte erfordern ein gültiges Anbieter-Authentifizierungstoken.

---

# eSIM-Statuszuordnung

Das Backend gibt interne Statuswerte zurück, die mit benutzerfreundlichen Bezeichnungen angezeigt werden sollten.

| Backend-Status           | Anzeigename            |
| ------------------------ | ---------------------- |
| \`waiting_for_assignment\` | Auf Lager              |
| \`pre_service\`            | Wartet auf Aktivierung |
| \`in_service\`             | Aktiv                  |
| \`terminated\`             | Beendet                |

---

# Anfragestatus-Zuordnung

| Status     | Bedeutung                                     |
| ---------- | --------------------------------------------- |
| \`pending\`  | Anfrage eingereicht und wartet auf Genehmigung |
| \`approved\` | Anfrage genehmigt und eSIMs zum Inventar hinzugefügt |
| \`rejected\` | Anfrage abgelehnt                             |

---

# Anbieter-Workflow

\`\`\`text
SIMs anfordern
      ↓
Anfragegenehmigung
      ↓
eSIM zum Inventar hinzugefügt
      ↓
eSIM dem Kunden zuweisen
      ↓
QR-Code generieren
      ↓
Kunde aktiviert eSIM
      ↓
Status synchronisieren
      ↓
eSIM wird aktiv
\`\`\`

---

# 1. eSIMs anfordern

Anbieter können Anfragen für neues eSIM-Inventar einreichen.

### API

\`\`\`http
POST https://r4lsolutions.com/backend/api/provider/sim-requests
\`\`\`

### Anfragekörper

\`\`\`json
{
  "quantity": 5,
  "note": "Need for Q3 campaign"
}
\`\`\`

### Validierung

| Feld     | Typ    | Erforderlich | Hinweise            |
| -------- | ------ | -------- | ------------------- |
| quantity | Zahl   | Ja       | Bereich: 1–500      |
| note     | Text   | Nein     | Zusätzliche Kommentare |

### Erwartetes Verhalten

* Eine neue Anfrage erstellen.
* Der Anfragestatus wird automatisch auf \`pending\` gesetzt.
* Die neu erstellte Anfrage sollte in der Anfrageliste erscheinen.

---

# 2. Meine SIM-Anfragen anzeigen

Anbieter können eingereichte Anfragen verfolgen und den Genehmigungsstatus überwachen.

### API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests
\`\`\`

### Beispiel

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests?page=1&pageSize=20&status=pending
\`\`\`

### Verfügbare Filter

| Filter    | Beschreibung                |
| --------- | --------------------------- |
| status    | pending, approved, rejected |
| search    | Suche nach Anfrage-ID       |
| startDate | Nach Erstellungsdatum filtern |
| endDate   | Nach Erstellungsdatum filtern |

### Statistik-API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/sim-requests/stats
\`\`\`

### UI-Anforderungen

Anzeigen:

* Anfrage-ID
* Menge
* Notizen
* Erstellungsdatum
* Status-Badge

### Status-Badges

* Ausstehend
* Genehmigt
* Abgelehnt

> Sobald eine Anfrage genehmigt wird, stehen die zugehörigen eSIMs automatisch im Inventarbereich zur Verfügung.

---

# 3. eSIM-Inventarverwaltung

Das Inventar zeigt alle dem Anbieter zugewiesenen eSIMs an.

### API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims
\`\`\`

### Beispiel

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims?assigned=false&status=pre_service&page=1&pageSize=20
\`\`\`

### Verfügbare Filter

| Filter    | Beschreibung      |
| --------- | ----------------- |
| assigned  | true / false      |
| status    | eSIM-Status       |
| search    | ICCID-Suche       |
| startDate | Datumsbereichsfilter |
| endDate   | Datumsbereichsfilter |

### Statistik-API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/esims/stats
\`\`\`

### Antwortfelder

| Feld            | Beschreibung                 |
| --------------- | ---------------------------- |
| iccid           | Eindeutige eSIM-Kennung      |
| activation_code | Aktivierungscode (LPA-Zeichenkette) |
| status          | Aktueller eSIM-Status        |
| customer_id     | Zugewiesener Kunde           |
| activated_at    | Aktivierungszeitstempel      |

### UI-Anforderungen

Anzeigen:

* ICCID
* Status
* Zugewiesener Kunde
* Aktivierungsdatum
* Aktionen

### Aktionen

| Aktion      | Verfügbarkeit         |
| ----------- | --------------------- |
| Assign      | Nicht zugewiesene eSIMs |
| Sync Status | Alle Inventardatensätze |

### Inventaransichten

**Zur Zuweisung verfügbar**

\`\`\`text
assigned = false
\`\`\`

Zeigt eSIMs an, die Kunden zugewiesen werden können.

---

# 4. eSIM einem Kunden zuweisen

Dieser Schritt verknüpft eine verfügbare eSIM mit einem Kunden und generiert Aktivierungsdaten.

### API

\`\`\`http
PATCH https://r4lsolutions.com/backend/api/provider/esims/{iccid}/assign
\`\`\`

### Anfragekörper

\`\`\`json
{
  "customer_id": "cust_abc123"
}
\`\`\`

### Antwort

Gibt zurück:

* Informationen zur zugewiesenen eSIM
* Aktivierungscode (LPA-Zeichenkette)

Beispiel:

\`\`\`text
LPA:1$consumer.e-sim.global$TN2025...
\`\`\`

---

## Frontend-Anforderungen nach Zuweisung

### QR-Code generieren

Der zurückgegebene \`activation_code\` muss in einen QR-Code umgewandelt werden.

### Aktivierungscode anzeigen

Den rohen Aktivierungscode als Fallback-Option bereitstellen.

Beispiel:

\`\`\`text
LPA:1$consumer.e-sim.global$TN2025...
\`\`\`

### Kopierfunktion

Dem Anbieter erlauben, den Aktivierungscode zu kopieren.

### Wichtig

Das Zuweisen einer eSIM **aktiviert sie nicht**.

Der Status bleibt:

\`\`\`text
Wartet auf Aktivierung
\`\`\`

bis der Kunde das eSIM-Profil installiert.

---

# 5. Kundenaktivierung

Während der Aktivierung ist keine Aktion des Anbieters erforderlich.

### Prozess

1. Anbieter teilt QR-Code.
2. Kunde scannt QR-Code auf seinem Gerät.
3. Netzbetreiber installiert das eSIM-Profil.
4. Backend erhält Aktivierungsbestätigung.
5. Status ändert sich automatisch:

\`\`\`text
pre_service → in_service
\`\`\`

### Frontend-Anforderung

Kein API-Aufruf erforderlich.

Der Anbieter muss nur den Inventarstatus aktualisieren oder synchronisieren.

---

# 6. Aktivierungsstatus synchronisieren

Ermöglicht Anbietern, die neuesten Aktivierungsaktualisierungen vom Netzbetreiber abzurufen.

### API

\`\`\`http
POST https://r4lsolutions.com/backend/api/provider/esims/sync
\`\`\`

### Beispielantwort

\`\`\`json
{
  "total_checked": 5,
  "total_updated": 2,
  "updated": [
    {
      "iccid": "8910300000041170580",
      "old_status": "pre_service",
      "new_status": "in_service",
      "activated_at": "2026-06-17T08:00:00.000Z"
    }
  ],
  "failed": []
}
\`\`\`

### Frontend-Verhalten

Nach erfolgreicher Synchronisierung:

1. Inventardaten neu laden.
2. Status-Badges aktualisieren.
3. Aktivierungszeitstempel anzeigen.
4. Aktivierte eSIMs in den Status Aktiv verschieben.

### Statusübergang

\`\`\`text
Wartet auf Aktivierung
          ↓
       Aktiv
\`\`\`

---

# Kundenverwaltung

Anbieter können Kunden über das Kundenmodul verwalten.

### Kundenlisten-API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/customers
\`\`\`

### Kundenstatistik-API

\`\`\`http
GET https://r4lsolutions.com/backend/api/provider/customers/stats
\`\`\`

---

# Vollständige API-Referenz

## SIM-Anfragen

| Methode | Endpoint                           | Beschreibung           |
| ------ | ---------------------------------- | ---------------------- |
| POST   | \`https://r4lsolutions.com/backend/api/provider/sim-requests\`       | Neue SIM-Anfrage erstellen |
| GET    | \`https://r4lsolutions.com/backend/api/provider/sim-requests\`       | SIM-Anfragen auflisten |
| GET    | \`https://r4lsolutions.com/backend/api/provider/sim-requests/stats\` | Anfragestatistiken     |

---

## eSIM-Inventar

| Methode | Endpoint                             | Beschreibung            |
| ------ | ------------------------------------ | ----------------------- |
| GET    | \`https://r4lsolutions.com/backend/api/provider/esims\`                | eSIM-Inventar auflisten |
| GET    | \`https://r4lsolutions.com/backend/api/provider/esims/stats\`          | Inventarstatistiken     |
| PATCH  | \`https://r4lsolutions.com/backend/api/provider/esims/{iccid}/assign\` | eSIM dem Kunden zuweisen |
| POST   | \`https://r4lsolutions.com/backend/api/provider/esims/sync\`           | Aktivierungsstatus synchronisieren |

---

## Kunden

| Methode | Endpoint                        | Beschreibung        |
| ------ | ------------------------------- | ------------------- |
| GET    | \`https://r4lsolutions.com/backend/api/provider/customers\`       | Kundenliste         |
| GET    | \`https://r4lsolutions.com/backend/api/provider/customers/stats\` | Kundenstatistiken   |

---

# End-to-End-Lebenszyklus

\`\`\`text
Anbieter fordert eSIMs an
          ↓
Anfrage genehmigt
          ↓
eSIM zum Inventar hinzugefügt
          ↓
Anbieter weist eSIM zu
          ↓
QR-Code generiert
          ↓
Kunde installiert eSIM
          ↓
Netzbetreiber bestätigt Aktivierung
          ↓
Anbieter synchronisiert Status
          ↓
eSIM wird aktiv
\`\`\`

Dieser Workflow ermöglicht es Anbietern, Inventar effizient zu verwalten, eSIMs an Kunden zu verteilen und den Aktivierungsfortschritt über ein einziges Portal zu überwachen.
`;

export default guide;
